import { useEffect, useRef } from "react";
import {
  BaselineSeries,
  CandlestickSeries,
  LineSeries,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from "lightweight-charts";

import { resolveChartColors } from "./chartTheme";
import type {
  ChartTimeframe,
  NewsSignalFinbertPoint,
  NewsSignalL6Point,
  PriceBar,
} from "./types";

export interface ChartHostProps {
  timeframe: ChartTimeframe;
  tz: string;
  bars: PriceBar[];
  l6: NewsSignalL6Point[];
  finbert: NewsSignalFinbertPoint[];
  onCrosshairMove: (info: CrosshairInfo | null) => void;
  showFinbert?: boolean;
  /** Tick-size-aware display precision (chartTheme.pricePrecisionFor). */
  pricePrecision?: number;
}

export interface CrosshairInfo {
  t: Time;
  price?: { o: number; h: number; l: number; c: number };
  l6?: NewsSignalL6Point;
  finbert?: NewsSignalFinbertPoint;
}

const PRICE_PANE = 0;
const SIGNAL_PANE = 1;

function isoToUnix(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 1000);
}

/**
 * ChartHost — the only file in the chart feature that imports
 * lightweight-charts. Owns mount / unmount, series creation, data
 * push-down, and crosshair callback wiring. Spec §8.2 / §8.5 / §8.7.
 */
export function ChartHost({
  timeframe,
  tz,
  bars,
  l6,
  finbert,
  onCrosshairMove,
  showFinbert = true,
  pricePrecision,
}: ChartHostProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<{
    candles: ISeriesApi<"Candlestick">;
    l6: ISeriesApi<"Baseline">;
    finbert: ISeriesApi<"Line">;
  } | null>(null);

  // Mount once; cleanup on unmount.
  useEffect(() => {
    if (!hostRef.current) return;
    // Canvas can't consume CSS classes — resolve the app tokens here so
    // the chart matches the active theme instead of default-light chrome.
    const colors = resolveChartColors();
    const chart = createChart(hostRef.current, {
      autoSize: true,
      layout: {
        background: { color: colors.background },
        textColor: colors.text,
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      rightPriceScale: { borderColor: colors.grid },
      timeScale: { borderColor: colors.grid },
      localization: {
        // calendar.tz reuse: format crosshair times in the resolved tz.
        timeFormatter: (time: Time) => {
          const ms = typeof time === "number" ? time * 1000 : NaN;
          if (Number.isNaN(ms)) return String(time);
          try {
            return new Intl.DateTimeFormat(undefined, {
              timeZone: tz,
              year: "numeric", month: "short", day: "2-digit",
              hour: "2-digit", minute: "2-digit",
              hour12: false,
            }).format(new Date(ms));
          } catch {
            return new Date(ms).toISOString();
          }
        },
      },
    });
    chart.addPane();
    // paneIndex is the 3rd positional arg of addSeries in v5 — passing it
    // inside the options object is silently ignored and every series lands
    // in pane 0.
    const precision = pricePrecision ?? 2;
    const candleSeries = chart.addSeries(
      CandlestickSeries,
      {
        upColor: colors.bull,
        downColor: colors.bear,
        wickUpColor: colors.bull,
        wickDownColor: colors.bear,
        borderVisible: false,
        priceFormat: {
          type: "price",
          precision,
          minMove: 10 ** -precision,
        },
      },
      PRICE_PANE,
    );
    // Baseline series: two-tone around 0 — bullish above, bearish below —
    // instead of a single always-green line that read "bullish" even at
    // score -0.9. Fixed ±1 scale: the signal is bounded by construction;
    // autoscale would zoom a quiet ±0.1 day into misleading drama.
    const l6Series = chart.addSeries(
      BaselineSeries,
      {
        baseValue: { type: "price", price: 0 },
        topLineColor: colors.bull,
        bottomLineColor: colors.bear,
        topFillColor1: "transparent",
        topFillColor2: "transparent",
        bottomFillColor1: "transparent",
        bottomFillColor2: "transparent",
        priceFormat: { type: "price", precision: 2, minMove: 0.01 },
        autoscaleInfoProvider: () => ({
          priceRange: { minValue: -1.05, maxValue: 1.05 },
        }),
      },
      SIGNAL_PANE,
    );
    l6Series.createPriceLine({
      price: 0,
      color: colors.grid,
      lineWidth: 1,
      lineStyle: 1, // LineStyle.Dotted
      axisLabelVisible: false,
      title: "",
    });
    const finbertSeries = chart.addSeries(
      LineSeries,
      { color: colors.finbert, lineWidth: 1 },
      SIGNAL_PANE,
    );

    chartRef.current = chart;
    seriesRef.current = { candles: candleSeries, l6: l6Series, finbert: finbertSeries };

    // The app toggles `.light` on <html> at runtime; colors were captured
    // at construction, so re-resolve and re-style when the class changes.
    const observer = new MutationObserver(() => {
      const c = resolveChartColors();
      chart.applyOptions({
        layout: { background: { color: c.background }, textColor: c.text },
        grid: { vertLines: { color: c.grid }, horzLines: { color: c.grid } },
        rightPriceScale: { borderColor: c.grid },
        timeScale: { borderColor: c.grid },
      });
      candleSeries.applyOptions({
        upColor: c.bull, downColor: c.bear,
        wickUpColor: c.bull, wickDownColor: c.bear,
      });
      l6Series.applyOptions({ topLineColor: c.bull, bottomLineColor: c.bear });
      finbertSeries.applyOptions({ color: c.finbert });
    });
    observer.observe(document.documentElement, {
      attributes: true, attributeFilter: ["class"],
    });

    chart.subscribeCrosshairMove((param: { time?: Time }) => {
      if (!param || param.time === undefined) {
        onCrosshairMove(null);
        return;
      }
      onCrosshairMove({ t: param.time });
    });

    return () => {
      observer.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
    // Recreating the chart on tz change is correct — the timeFormatter
    // is captured at construction. Other props re-push data only.
    // pricePrecision is per-material and fixed for a mounted chart, but
    // keeping it in deps makes a future material switch rebuild correctly.
  }, [tz, onCrosshairMove, pricePrecision]);

  // Push data on every change. Replacing the whole series is fine at
  // our sizes (≤5000 points) — spec §9.4.
  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    s.candles.setData(bars.map((b) => ({
      time: isoToUnix(b.t) as Time,
      open: b.o, high: b.h, low: b.l, close: b.c,
    })));
    s.l6.setData(
      l6
        .filter((p) => p.score !== null)
        .map((p) => ({ time: isoToUnix(p.t) as Time, value: p.score as number })),
    );
    s.finbert.setData(
      showFinbert
        ? finbert
            .filter((p) => p.score !== null)
            .map((p) => ({ time: isoToUnix(p.t) as Time, value: p.score as number }))
        : [],
    );
  }, [bars, l6, finbert, showFinbert, timeframe]);

  return (
    <div
      ref={hostRef}
      data-testid="chart-host"
      style={{ width: "100%", height: 380 }}
    />
  );
}
